/**
 * The `plot_chart` spec, as the renderer reads it. sphere-mcp defines and answers the tool
 * (src/server/chat/plot-chart.ts); the spec — the tool-call *input* — is rendered here as an
 * interactive Highcharts chart by the tool UI registered in `SphereAIChart`.
 */
import { z } from "zod";

export const PLOT_CHART_TOOL_NAME = "plot_chart";

/** A single data point: a bare number (aligned to `categories`) or a labelled `{name, y}` (pie / labelled points). */
const dataPointSchema = z.union([z.number(), z.object({ name: z.string(), y: z.number() })]);

const seriesSchema = z.object({
	name: z.string(),
	data: z.array(dataPointSchema).min(1),
});

export const chartTypeSchema = z.enum(["line", "area", "column", "bar", "pie", "scatter"]);
export type ChartType = z.infer<typeof chartTypeSchema>;

export const chartSpecSchema = z.object({
	chartType: chartTypeSchema,
	title: z.string().optional(),
	subtitle: z.string().optional(),
	xAxisTitle: z.string().optional(),
	yAxisTitle: z.string().optional(),
	/** X-axis labels shared by every series (line/area/column/bar/scatter). Ignored for pie. */
	categories: z.array(z.string()).optional(),
	/** Stack series on top of each other (column/bar/area only). */
	stacked: z.boolean().optional(),
	series: z.array(seriesSchema).min(1),
});

export type ChartSpec = z.infer<typeof chartSpecSchema>;
export type ChartSeries = z.infer<typeof seriesSchema>;

/** Validate a tool-call input as a chart spec. Returns the parsed spec or a human-readable error. */
export function parseChartSpec(input: unknown): { ok: true; spec: ChartSpec } | { ok: false; error: string } {
	const parsed = chartSpecSchema.safeParse(input);
	if (parsed.success) return { ok: true, spec: parsed.data };
	const error = parsed.error.issues.map((i) => `${i.path.join(".") || "(root)"}: ${i.message}`).join("; ");
	return { ok: false, error };
}
